import type { OrgRow } from '~/gql/organizations/organizations'
import { useMutation, useQuery } from '@apollo/client/react'
import { OrganizationStatus } from '@zeus/index'
import { MoreHorizontal } from 'lucide-react'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { Badge } from '~/components/ui/badge'
import { Button } from '~/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '~/components/ui/dialog'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '~/components/ui/dropdown-menu'
import { Input } from '~/components/ui/input'
import { Label } from '~/components/ui/label'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '~/components/ui/table'
import {
  GQL_CREATE_ORGANIZATION,
  GQL_ORGANIZATIONS,
  GQL_SET_ORGANIZATION_STATUS,
} from '~/gql/organizations/organizations'
import { client } from '~/helpers/apollo'
import OrganizationDeleteModal from '~/pages/backoffice/admin/organizations/components/OrganizationDeleteModal'
import OrganizationEditModal from '~/pages/backoffice/admin/organizations/components/OrganizationEditModal'
import OrganizationMembersModal from '~/pages/backoffice/admin/organizations/components/OrganizationMembersModal'

type FormData = {
  name: string
  slug: string
  adminEmail: string
  adminFirstName: string
  adminLastName: string
}

function OrganizationsTable() {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const [editing, setEditing] = useState<OrgRow | null>(null)
  const [membersFor, setMembersFor] = useState<OrgRow | null>(null)
  const [deleting, setDeleting] = useState<OrgRow | null>(null)

  const { data, refetch } = useQuery(GQL_ORGANIZATIONS, { client, fetchPolicy: 'network-only' })
  const [createOrganization, { loading }] = useMutation(GQL_CREATE_ORGANIZATION, { client })
  const [setStatus] = useMutation(GQL_SET_ORGANIZATION_STATUS, { client })

  const { register, handleSubmit, reset, watch, setValue } = useForm<FormData>({
    defaultValues: { name: '', slug: '', adminEmail: '', adminFirstName: '', adminLastName: '' },
  })

  const organizations = (data?.organizations ?? []) as unknown as OrgRow[]

  const onCreate = handleSubmit(async (values) => {
    try {
      await createOrganization({ variables: { input: values } })
      toast.success(t('good.add.organization'))
      setOpen(false)
      reset()
      await refetch()
    } catch {
      toast.error(t('bad.add.organization'))
    }
  })

  const toggleStatus = async (org: OrgRow) => {
    const status = org.status === OrganizationStatus.ACTIVE ? OrganizationStatus.SUSPENDED : OrganizationStatus.ACTIVE
    try {
      await setStatus({ variables: { id: org.id, status } })
      await refetch()
    } catch {
      toast.error(t('unknow.error'))
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-lg font-semibold">{t('common.organizations')}</h1>
        <Button onClick={() => setOpen(true)}>{t('organization.create')}</Button>
      </div>

      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t('common.name')}</TableHead>
              <TableHead>{t('organization.slug')}</TableHead>
              <TableHead>{t('organization.members')}</TableHead>
              <TableHead>{t('common.status')}</TableHead>
              <TableHead className="w-[80px] text-right">{t('common.action')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {organizations.map(org => (
              <TableRow key={org.id}>
                <TableCell className="font-medium">{org.name}</TableCell>
                <TableCell className="text-muted-foreground">{org.slug}</TableCell>
                <TableCell>{org.userCount}</TableCell>
                <TableCell>
                  <Badge variant={org.status === OrganizationStatus.ACTIVE ? 'success' : 'secondary'}>
                    {org.status === OrganizationStatus.ACTIVE ? t('status.active') : t('organization.suspended')}
                  </Badge>
                </TableCell>
                <TableCell className="text-right">
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" className="size-8 p-0">
                        <span className="sr-only">{t('common.action')}</span>
                        <MoreHorizontal className="size-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => setEditing(org)}>
                        {t('common.update')}
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => setMembersFor(org)}>
                        {t('organization.members')}
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => toggleStatus(org)}>
                        {org.status === OrganizationStatus.ACTIVE ? t('organization.suspend') : t('organization.activate')}
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        className="text-destructive focus:text-destructive"
                        onClick={() => setDeleting(org)}
                      >
                        {t('common.delete')}
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* Création */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent aria-describedby={undefined}>
          <DialogHeader>
            <DialogTitle>{t('organization.create')}</DialogTitle>
          </DialogHeader>
          <form onSubmit={onCreate} className="space-y-4">
            <p className="text-sm text-muted-foreground">{t('organization.create.help')}</p>
            <div className="space-y-2">
              <Label htmlFor="name">{t('common.name')}</Label>
              <Input id="name" {...register('name', { required: true })} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="slug">{t('organization.slug')}</Label>
              <Input
                id="slug"
                {...register('slug', { required: true })}
                onChange={e => setValue('slug', e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-'))}
                value={watch('slug')}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="adminEmail">{t('organization.admin.email')}</Label>
              <Input id="adminEmail" type="email" {...register('adminEmail', { required: true })} />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-2">
                <Label htmlFor="adminFirstName">{t('common.firstname')}</Label>
                <Input id="adminFirstName" {...register('adminFirstName', { required: true })} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="adminLastName">{t('common.lastname')}</Label>
                <Input id="adminLastName" {...register('adminLastName', { required: true })} />
              </div>
            </div>
            <div className="flex justify-end space-x-2">
              <Button variant="outline" type="button" onClick={() => setOpen(false)}>
                {t('common.cancel')}
              </Button>
              <Button type="submit" disabled={loading}>{t('common.create')}</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {editing && (
        <OrganizationEditModal
          open={!!editing}
          onCancel={() => setEditing(null)}
          onSuccess={refetch}
          org={editing}
        />
      )}

      {membersFor && (
        <OrganizationMembersModal
          open={!!membersFor}
          onCancel={() => setMembersFor(null)}
          onChanged={refetch}
          org={membersFor}
        />
      )}

      {deleting && (
        <OrganizationDeleteModal
          open={!!deleting}
          onCancel={() => setDeleting(null)}
          onSuccess={refetch}
          org={deleting}
        />
      )}
    </div>
  )
}

export default OrganizationsTable
